import React, { useState, useCallback, useEffect } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Eye, FileText, Info } from "lucide-react";
import { Button } from "../../components/ui/Button";
import ViewWebsiteButton from "../../components/ui/ViewWebsiteButton";
import SectionHeader from "../../components/ui/SectionHeader";
import FormField from "../../components/ui/FormField";
import { Textarea } from "../../components/ui/Textarea";
import { AboutSectionFieldLimits } from "../../config/fieldlimit";
import { useAuth } from "../../auth/AuthContext";
import { getWebsiteSettings, updateAboutPage } from "../../services/websitesetting";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import InfoContainer from "../../components/ui/InfoContainer";
import WebsiteSectionCard from "../../components/ui/WebsiteSectionCard";
import PageHeader from "../../components/ui/PageHeader";

const MAX_DESC = AboutSectionFieldLimits.description.max;

const AboutPageSettings: React.FC = () => {
  const { user } = useAuth();
  const { subdomain } = useOutletContext<{ subdomain: string }>();
  const [formData, setFormData] = useState({
    heading: "",
    description: "",
  });
  const toast = useToast();
  const [errors, setErrors] = useState<{ heading?: string; description?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const charCount = formData.description?.length ?? 0;
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await getWebsiteSettings();
        if (data?.about_us_page) {
          const { heading, description } = data.about_us_page;
          setFormData({
            heading: heading || "",
            description: description || "",
          });
        }
      } catch (error: any) {
        console.error("fetchData ERROR:", error);
        console.error("fetchData Response:", error.response);
        console.error("fetchData Response Data:", error.response?.data);
        const status = error.response?.status;
        if (status === 404) {
          toast.error("About Us page settings not found. Using defaults.");
        } else {
          toast.error("Failed to load About Us page settings.");
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // -------------------- Validation --------------------
  const validate = () => {
    const newErrors: typeof errors = {};

    if (!formData.heading.trim()) {
      newErrors.heading = "Heading is required.";
    } else if (formData.heading.length > AboutSectionFieldLimits.heading.max) {
      newErrors.heading = `Heading cannot exceed ${AboutSectionFieldLimits.heading.max} characters.`;
    }

    if (!formData.description.trim() || formData.description.length < AboutSectionFieldLimits.description.min) {
      newErrors.description = `Description must be at least ${AboutSectionFieldLimits.description.min} characters`;
    } else if (formData.description.length > MAX_DESC) {
      newErrors.description = `Description cannot exceed ${MAX_DESC} characters`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // -------------------- Handlers --------------------
  const handleChange = (field: "heading" | "description", value: string) => {
    setFormData({ ...formData, [field]: value });
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = useCallback(async () => {
    if (!validate()) {
      toast.error("Please fix the validation errors.");
      return;
    }
    setIsSubmitting(true);

    try {
      await updateAboutPage({
        heading: formData.heading,
        description: formData.description,
      });
      toast.success("About Us page updated successfully!");
    } catch (error: any) {
      console.error("handleSubmit ERROR:", error);
      console.error("handleSubmit Response:", error.response);
      console.error("handleSubmit Response Data:", error.response?.data);

      if (error?.type === "serverValidation") {
        setErrors(error.errors);
        toast.error("Please fix the validation errors.");
      } else {
        const status = error.response?.status;
        let userMessage = "Failed to update About Us page.";

        if (status === 404) {
          userMessage = "About Us page data not found. Please refresh and try again.";
        } else if (status === 403) {
          userMessage = "You don't have permission to modify this page.";
        } else if (status === 422) {
          userMessage = "Invalid data provided. Please check all fields.";
        } else if (status === 500) {
          userMessage = "Internal server error. Please try again soon.";
        } else if (error.message === "Network Error") {
          userMessage = "Network problem detected. Check your connection.";
        }

        toast.error(userMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formData]);

  return (
    <>
      {(isLoading || isSubmitting) && (
        <GlobalLoader
          initialPageLoad={isLoading}
          fullScreen={isSubmitting}
          description={isSubmitting ? "Saving changes..." : "Loading about page..."}
        />
      )}
      <div className="space-y-5">

        {/* Header Card */}
        <PageHeader
          title="About Us Page"
          description="Customize the dedicated About Us page for your website"
          variant="card"
          className="mb-5"
          mobileActionsInHeader={true}
          actions={
            <ViewWebsiteButton subdomain={subdomain} />
          }
        />

        {/* Main Content */}
        <WebsiteSectionCard
          title="Page Content"
          icon={FileText}
        >
          <div className="space-y-6">
            <div>
              <SectionHeader title="Main Content" />

              <InfoContainer
                message="This content will appear on your dedicated About Us page"
                iconSize={13}
                className="mb-4"
              />

              <div className="space-y-5">
                <FormField
                  label="Page Heading *"
                  name="heading"
                  value={formData.heading}
                  onChange={(_, val) => handleChange("heading", val)}
                  type="text"
                  placeholder="e.g. Our Story, Who We Are"
                  error={errors.heading}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    placeholder="Tell your story here..."
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value.slice(0, MAX_DESC))}
                    className={`w-full min-h-[250px] resize-y ${errors.description ? "border-red-500 focus:border-red-500 focus:ring-red-200" : ""}`}
                  />
                  <div className="flex justify-between items-center mt-1.5">
                    <span className={`text-xs ${charCount >= 150 ? "text-green-600 font-medium" : "text-red-500"}`}>
                      {charCount >= 150 ? "✓ Minimum length met" : `Need ${150 - charCount} more characters`}
                    </span>
                    <span className="text-xs text-gray-500 font-mono">{charCount}/{MAX_DESC}</span>
                  </div>
                  {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="pt-4 border-t border-gray-100 flex justify-end">
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2.5 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </WebsiteSectionCard>
      </div>
    </>
  );
};

export default AboutPageSettings;
